// Charges des opérations saisies sur l'appareil, construites et contrôlées par les schémas du contrat
// (@cc/contrats : schemaRealisationTournee, schemaConstat, verifierGrilleConstat) avant d'entrer dans la
// file : le serveur les recevra telles quelles. Fonctions pures, testées seules.
import {
  schemaConstat,
  schemaRealisationTournee,
  verifierGrilleConstat,
  type ChargeRapportMission,
  type EtatRealisation,
  type OrdreMissionTerrain,
  type RealisationTournee,
  type ReponsePoint,
  type SaisieConstat,
  type TourneeTerrain,
} from '@cc/contrats';
import { positionContrat, type PositionTerrain } from './appareil';
import { heure } from './etat-local';

/** Matériaux saisis librement (« 2 sacs de ciment, lampe 150 W ») : un élément par virgule, quantité lue en tête si elle est donnée (D-132). */
export function lireMateriaux(texte: string): ChargeRapportMission['contenu']['materiaux'] {
  return texte
    .split(/[,;\n]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 100)
    .map((s) => {
      const m = /^(\d+(?:[.,]\d+)?)\s+(.+)$/.exec(s);
      return m ? { designation: (m[2] as string).slice(0, 200), quantite: Number((m[1] as string).replace(',', '.')) } : { designation: s.slice(0, 200) };
    });
}

/** Pointage d'un point de collecte sur l'appareil (D-126). */
export interface PointagePoint {
  point_id: string;
  collecte: boolean;
  heure: string | null;
  observation: string | null;
}

/** Réalisation du contrat d'après le pointage : points collectés dans l'ordre, pointage résumé dans les observations, début et fin aux heures de pointage. */
export function chargeRealisation(t: TourneeTerrain, pointage: Record<string, PointagePoint>, etat: EtatRealisation, motif: string): RealisationTournee {
  const points = [...t.points].sort((a, b) => a.ordre - b.ordre);
  const heures = points.map((p) => pointage[p.id]?.heure).filter((h): h is string => Boolean(h)).sort();
  const notes = points
    .filter((p) => pointage[p.id]?.observation || pointage[p.id]?.heure)
    .map((p) => `${p.code}${pointage[p.id]?.heure ? ` (${heure(pointage[p.id]?.heure)})` : ''}${pointage[p.id]?.observation ? ` : ${pointage[p.id]?.observation}` : ''}`);
  return schemaRealisationTournee.parse({
    date: t.date,
    etat,
    ...(etat === 'realisee' ? {} : { motif: motif.trim() }),
    points_collectes: points.filter((p) => pointage[p.id]?.collecte).map((p) => p.id),
    ...(notes.length ? { observations: notes.join(' ; ').slice(0, 2000) } : {}),
    ...(heures.length ? { debut: heures[0], fin: heures[heures.length - 1] } : {}),
  });
}

/** Réponse saisie pour un point de contrôle (code du point du formulaire). */
export interface ReponseSaisie {
  valeur: ReponsePoint | '';
  observation: string;
  /** Identifiants client des photos du point ; la première est citée par photo_id_client. */
  photos: string[];
}

/** Problèmes de la grille selon la règle du contrat (verifierGrilleConstat, D-122), champ par code de point. */
export function problemesGrille(o: OrdreMissionTerrain, reponses: Record<string, ReponseSaisie>): { code: string | null; message: string }[] {
  const saisies = o.formulaire.points.filter((p) => reponses[p.code]?.valeur).map((p) => versReponse(p.code, reponses[p.code] as ReponseSaisie));
  return verifierGrilleConstat(o.formulaire.points, saisies).map((pb) => {
    const i = /^reponses\.(\d+)\./.exec(pb.champ);
    return { code: i ? (saisies[Number(i[1])]?.code ?? null) : null, message: pb.message };
  });
}

function versReponse(code: string, r: ReponseSaisie): SaisieConstat['reponses'][number] {
  return {
    code,
    reponse: r.valeur as ReponsePoint,
    ...(r.observation.trim() ? { observation: r.observation.trim() } : {}),
    ...(r.photos[0] ? { photo_id_client: r.photos[0] } : {}),
  };
}

/** Constat du contrat : réponses par code, observations, position, heure, mesure proposée si non conforme (D-133). */
export function chargeConstat(o: OrdreMissionTerrain, reponses: Record<string, ReponseSaisie>, extras: { observations: string; position: PositionTerrain | null; mesure: { description: string; delai_jours: number } | null; effectue_le: string }): SaisieConstat {
  return schemaConstat.parse({
    reponses: o.formulaire.points.map((p) => versReponse(p.code, reponses[p.code] ?? { valeur: '', observation: '', photos: [] })),
    ...(extras.observations.trim() ? { observations: extras.observations.trim() } : {}),
    ...(extras.position ? { position: positionContrat(extras.position) } : {}),
    effectue_le: extras.effectue_le,
    ...(extras.mesure ? { mesure_proposee: { description: extras.mesure.description.trim(), delai_jours: extras.mesure.delai_jours } } : {}),
  });
}
